/** @format */

/* eslint react-hooks/rules-of-hooks: "warn", @typescript-eslint/prefer-nullish-coalescing: "warn" */
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { ValidationError } from '@mpb-com/feather/components/atoms/validation-error';
import React, {
    ForwardRefRenderFunction,
    HTMLAttributes,
    ReactNode,
    Ref,
    forwardRef,
    useRef,
} from 'react';
import styled from 'styled-components';

import CloseSVG from '@toucan/client/global/icons/close.svg';

import { propValidator } from '@toucan/client/global/emotion';

import { CharacterCounter } from './components/character-counter';
import { helpTextStyles, styles } from './input.styles';

export enum InputSize {
    MEDIUM = 'medium',
    LARGE = 'large',
}

export type InputProps = {
    /**
     * The form field label
     */
    label?: string;

    /**
     * Props to be applied to the label and which take priority
     */
    labelProps?: Record<string, any>;

    /**
     * The form field name
     */
    name: string;

    /**
     * The html element ID, must be unique
     */
    id?: string;

    /**
     * The input type
     * @default text
     */
    type?: 'text' | 'number' | 'password' | 'email' | 'tel' | 'search';

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Whether the button is disabled
     *
     * @default false
     */
    disabled?: boolean;

    /**
     * Value of the inputs autocomplete attribute
     */
    autoComplete?: string;

    /**
     * Whether this field is required
     *
     * @default false
     */
    required?: boolean;

    /**
     * The value of the input
     * html inputs can only be strings
     */
    value?: string;

    /**
     * Force the input to show an invalid state without having to pass
     * an error message to display.
     */
    forceError?: boolean;

    /**
     * The error message to show when the input is invalid.
     */
    error?: ReactNode;

    /**
     * Whether the input has been touched. Used by validation to update error messages
     */
    touched?: boolean;

    /**
     * The class name of the input, used to control styles
     */
    className?: string;

    /**
     * An icon to show before the input content
     */
    prefixIcon?: ReactNode;

    /**
     * An icon to show after the input content
     */
    suffixIcon?: ReactNode;

    /**
     * The size of the input, one of:
     * 1. MEDIUM
     * 2. LARGE
     *
     * @default MEDIUM
     */
    inputSize?: InputSize;

    /**
     * The label for the clear button. required along with an onClear event
     * handler for the clear button to be usable.
     */
    clearButtonLabel?: string;

    /**
     * If the component should always show the clear button
     */
    alwaysShowClearButton?: boolean;

    /**
     * True if the input value cannot be manually changed
     */
    readOnly?: boolean;

    /**
     * True to render a CharacterCount component for text input
     */
    showCharacterCounter?: boolean;

    /**
     * Max character count allowed
     */
    maxCharacterCount?: number;

    /**
     * Should the input be trimmed of whitespace on blur
     * @default true
     */
    trimOnBlur?: boolean;

    /**
     * Element change event handler.
     *
     * @param event {ChangeEvent<HTMLElement>} The change event
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
     * Element blur event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The blur event
     */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
     * Element focus event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The focus event
     */
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
     * Element focus event handler.
     *
     * @param event {MouseEvent<HTMLElement>} The focus event
     */
    onClear?: (event: React.MouseEvent<HTMLElement>) => void;
    placeholder?: string;
    helpText?: React.ReactElement | string;
    inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
};

const StyledInputContainer = styled.div`
    position: relative;
    display: block;
`;

const StyledInput = styled('input').withConfig({
    shouldForwardProp: (prop) =>
        propValidator(['onClear', 'aria-labelledby'])(prop),
})`
    ${styles}
`;

const StyledHelpText = styled.p`
    ${helpTextStyles}
`;

const InputWithRef: ForwardRefRenderFunction<HTMLInputElement, InputProps> = (
    {
        type = 'text',
        id,
        name,
        label,
        labelProps = {},
        prefixIcon,
        suffixIcon,
        inputSize,
        error,
        touched,
        onClear,
        clearButtonLabel,
        disabled,
        className,
        autoComplete,
        required = false,
        alwaysShowClearButton = false,
        value,
        showCharacterCounter = false,
        maxCharacterCount = 100,
        trimOnBlur = true,
        placeholder,
        helpText,
        inputMode,
        ...otherProps
    }: InputProps,
    ref: Ref<HTMLInputElement>
) => {
    const inputRef = ref || useRef<HTMLInputElement>(null);

    // show character counter when user has 40 chars left - number chosen by UX
    const shouldShowCharacterCounter =
        showCharacterCounter && value
            ? value.toString().length / maxCharacterCount >= 0.6
            : false;

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!trimOnBlur) {
            otherProps.onBlur?.(e);
            return;
        }

        const value = e.target.value;
        const trimmedValue = value?.trim();

        e.target.value = trimmedValue ?? '';
        otherProps.onBlur?.(e);

        if (value === trimmedValue) {
            return;
        }

        otherProps.onChange?.(e);
    };

    const shouldShowHelpText = !(error && touched) && Boolean(helpText);

    return (
        <ValidationError
            disabled={disabled}
            error={error}
            touched={touched}
            className={className}
            data-testid={otherProps['data-testid']}
        >
            <StyledInputContainer>
                <StyledInput
                    type={type}
                    id={id || name}
                    name={name}
                    error={error}
                    touched={touched}
                    onClear={onClear}
                    prefixIcon={prefixIcon}
                    inputSize={inputSize}
                    disabled={disabled}
                    aria-required={required}
                    ref={inputRef}
                    autoComplete={autoComplete}
                    alwaysShowClearButton={alwaysShowClearButton}
                    value={value}
                    maxCharacterCount={maxCharacterCount}
                    showCharacterCounter={shouldShowCharacterCounter}
                    placeholder={placeholder}
                    inputMode={inputMode}
                    {...otherProps}
                    onBlur={handleBlur} // leave this one last so it can override otherProps and add trim
                />
                {label && (
                    <label htmlFor={id || name}>
                        {label}
                        {required && <sup>*</sup>}
                    </label>
                )}
                {onClear && value && clearButtonLabel && (
                    <Icon
                        className="input-clear"
                        aria-label={clearButtonLabel}
                        variant={IconVariant.DECORATIVE}
                        onClick={onClear}
                        disabled={disabled}
                        size={
                            inputSize === InputSize.LARGE
                                ? IconSize.M
                                : IconSize.S
                        }
                    >
                        <CloseSVG />
                    </Icon>
                )}
                {prefixIcon && <div className="prefix">{prefixIcon}</div>}
                {suffixIcon}
            </StyledInputContainer>
            {shouldShowCharacterCounter && (
                <CharacterCounter
                    count={`${value ?? ''}`.length}
                    max={maxCharacterCount}
                />
            )}
            {shouldShowHelpText && <StyledHelpText>{helpText}</StyledHelpText>}
        </ValidationError>
    );
};

export const Input = forwardRef(InputWithRef);
